import {TFunction} from 'i18next';
import {TypeEvenementDevis} from 'api-types/ffconnect';

/**
 * Génère une description pour un event d'historique de devis
 */
export function generateEventDescription(
  type: TypeEvenementDevis,
  eventData: Record<string, unknown>,
  t: TFunction,
): string {
  switch (type) {
    case 'devis.cree':
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_cree',
      );

    case 'article-devis.ajoute': {
      const {codeArticle, libelle} = eventData;
      if (codeArticle && libelle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.produit_avec_libelle_ajoute',
          {
            codeArticle,
            libelle,
          },
        );
      } else if (codeArticle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.produit_ajoute',
          {
            codeArticle,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.produit_ajoute_generique',
      );
    }

    case 'article-devis.supprime': {
      const {codeArticle, libelle} = eventData;
      if (codeArticle && libelle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.produit_avec_libelle_supprime',
          {
            codeArticle,
            libelle,
          },
        );
      } else if (codeArticle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.produit_supprime',
          {
            codeArticle,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.produit_supprime_generique',
      );
    }

    case 'devis.vide':
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_vide',
      );

    case 'ligne-devis.note-modifiee': {
      const {codeArticle, libelle} = eventData;
      if (codeArticle && libelle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.note_modifiee_avec_libelle',
          {
            codeArticle,
            libelle,
          },
        );
      } else if (codeArticle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.note_modifiee',
          {
            codeArticle,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.note_modifiee_generique',
      );
    }

    case 'ligne-devis.prix-modifie': {
      const {codeArticle, libelle, prixPrecedent, nouveauPrix} = eventData;
      if (codeArticle && libelle && prixPrecedent && nouveauPrix) {
        return t(
          'back_office.devis_personnalises.historique_evenements.prix_modifie_complet',
          {
            codeArticle,
            libelle,
            prixPrecedent,
            nouveauPrix,
          },
        );
      } else if (codeArticle) {
        return t(
          'back_office.devis_personnalises.historique_evenements.prix_modifie',
          {
            codeArticle,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.prix_modifie_generique',
      );
    }

    case 'devis.publie': {
      const {auteur_nom} = eventData;
      if (auteur_nom) {
        return t(
          'back_office.devis_personnalises.historique_evenements.devis_publie_par',
          {
            auteur_nom,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_publie',
      );
    }

    case 'devis.depublie': {
      const {auteur_nom} = eventData;
      if (auteur_nom) {
        return t(
          'back_office.devis_personnalises.historique_evenements.devis_depublie_par',
          {
            auteur_nom,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_depublie',
      );
    }

    case 'devis.archive':
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_archive',
      );

    // Messages commerciaux
    case 'message-devis.envoye': {
      const {expediteur_nom, destinataire_email} = eventData;
      if (expediteur_nom && destinataire_email) {
        return t(
          'back_office.devis_personnalises.historique_evenements.message_commercial_avec_contenu',
          {
            expediteur_nom,
            destinataire_email,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.message_commercial_envoye_generique',
      );
    }

    // Messages clients
    case 'commentaire-devis.envoye': {
      const {expediteur_nom} = eventData;
      if (expediteur_nom) {
        return t(
          'back_office.devis_personnalises.historique_evenements.message_client_recu',
          {
            expediteur_nom,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.message_client_recu_generique',
      );
    }

    case 'derogations.demandees': {
      const {expediteur_nom, email_manager, nombre_derogations} = eventData;
      const count = Number(nombre_derogations) || 0;
      if (expediteur_nom && email_manager) {
        return t(
          'back_office.devis_personnalises.historique_evenements.derogations_demandees_complet',
          {
            expediteur_nom,
            email_manager,
            count,
          },
        );
      } else if (expediteur_nom) {
        return t(
          'back_office.devis_personnalises.historique_evenements.derogations_demandees_par',
          {
            expediteur_nom,
            count,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.derogations_demandees_generique',
        {
          count,
        },
      );
    }

    case 'derogations.injectees': {
      const {injecte_par_nom, nombre_derogations_injectees} = eventData;
      const count = Number(nombre_derogations_injectees) || 0;
      if (injecte_par_nom) {
        return t(
          'back_office.devis_personnalises.historique_evenements.derogations_injectees_par',
          {
            injecte_par_nom,
            count,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.derogations_injectees_generique',
        {
          count,
        },
      );
    }

    case 'historique-devis.marque-lu':
      // Événement pour le marquage des messages comme lus, non affiché dans l'historique
      return '';

    case 'devis.consulte': {
      const {contact_prenom, contact_nom} = eventData;
      const nomComplet = [contact_prenom, contact_nom]
        .filter(Boolean)
        .join(' ');
      if (nomComplet) {
        return t(
          'back_office.devis_personnalises.historique_evenements.devis_consulte_par',
          {
            contact: nomComplet,
          },
        );
      }
      return t(
        'back_office.devis_personnalises.historique_evenements.devis_consulte',
      );
    }

    default:
      return t(
        'back_office.devis_personnalises.historique_evenements.evenement_inconnu',
      );
  }
}

/**
 * Récupère le contenu d'un message dans un event d'historique de devis
 */
export function getMessageContent(
  eventType: string,
  eventData: Record<string, unknown>,
): string | undefined {
  if (
    eventType === 'message-devis.envoye' ||
    eventType === 'commentaire-devis.envoye'
  ) {
    return eventData['contenu'] as string | undefined;
  }
  return undefined;
}
